import type { SystemStyleObject } from '@chakra-ui/react';

const DETAIL_SURFACE_FILL = 'rgba(96, 119, 156, 0.08)';
const DETAIL_SURFACE_SHADOW = '0 0 0 1px oklch(1 0 0 / 0.08)';
const DETAIL_SURFACE_HOVER_SHADOW =
  '0 0 0 1px var(--chakra-colors-glass-items-icon-hover)';

/**
 * Frosted panel for detail sections. Same 8% fill as glassy modals,
 * with a slightly stronger blur so the shell atmosphere stays visible.
 */
export const detailSurfaceSx: SystemStyleObject = {
  bg: DETAIL_SURFACE_FILL,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  borderWidth: 0,
  boxShadow: DETAIL_SURFACE_SHADOW,
  rounded: '2xl',
  transitionProperty: 'box-shadow',
  transitionDuration: '150ms',
  transitionTimingFunction: 'ease-out',
  '@supports not (backdrop-filter: blur(16px))': {
    bg: 'glass-items.bg-active',
  },
};

/**
 * Same glass rest as detail sections, plus the dashboard hover:
 * accent wash and accent ring. Portal evals stay on `detailSurfaceSx`.
 */
export const detailInteractiveSurfaceSx: SystemStyleObject = {
  ...detailSurfaceSx,
  transitionProperty: 'background-color, box-shadow',
  '@media (prefers-reduced-motion: no-preference)': {
    transitionDuration: '150ms',
    transitionTimingFunction: 'ease-out',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transitionDuration: '0.01ms',
  },
  _hover: {
    bg: 'cards.bg-hover',
    boxShadow: DETAIL_SURFACE_HOVER_SHADOW,
  },
};

export const detailInsetSurfaceSx: SystemStyleObject = {
  ...detailSurfaceSx,
  rounded: 'xl',
};

/** Interruptible background feedback for interactive detail rows. */
export const detailRowMotionSx: SystemStyleObject = {
  transitionProperty: 'background-color',
  '@media (prefers-reduced-motion: no-preference)': {
    transitionDuration: '150ms',
    transitionTimingFunction: 'ease-out',
  },
};

export const detailInteractiveRowSx: SystemStyleObject = {
  bg: 'glass-items.bg',
  ...detailRowMotionSx,
  _hover: {
    bg: 'glass-items.bg-hover',
  },
};

export const detailInteractiveCardSx: SystemStyleObject = {
  ...detailRowMotionSx,
  _hover: {
    bg: 'cards.bg-hover',
    borderColor: 'border.light',
  },
};

export const detailStaticCardSx: SystemStyleObject = {
  _hover: {
    bg: 'cards.bg',
    borderColor: 'border.light',
  },
};

/** Inset glass shell for list DataGrids. Rows stay transparent over atmosphere. */
export const listTableShellSx: SystemStyleObject = {
  ...detailSurfaceSx,
  mx: { base: 4, lg: 6 },
  mb: 4,
  overflow: 'hidden',
};

export const listTableGridSx: SystemStyleObject = {
  bg: 'transparent',
  thead: { bg: 'transparent' },
  tbody: { bg: 'transparent' },
  tr: { bg: 'transparent' },
  th: { bg: 'transparent', borderColor: 'border.light' },
  td: { bg: 'transparent', borderColor: 'border.light' },
};

export const listTableSlotProps = {
  container: {
    bg: 'transparent',
    sx: { bg: 'transparent' },
  },
  inner: {
    bg: 'transparent',
    sx: { bg: 'transparent' },
  },
  row: {
    bg: 'transparent',
  },
} as const;
